import ProductAccessories from './ProductAccessories';
import ProductImages from './ProductImages';
import Quantity from '../Quantity';
import { closeModal } from '../../helpers/modals';
import {
  cartApi,
  getTranslation,
  showQuickshop,
  updateCart,
} from '../../helpers';

const selectors = {
  mobileBlocks: '[data-mobile-blocks]',
  form: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  productSelect: '[data-product-select]',
  optionInput: '[name^="options"]',
  variantPicker: '.product-variant-picker',
  modalContent: 'quickshop-content',
  modalProduct: '[data-product]',
  complementaryProducts: '[data-complementary-products]',
};

const rootMargin = '0px 0px 200px 0px';
const limit = 4;

/**
 * The wrapper class for PDP components
 *
 * @export
 * @class Product
 */
export default class Product {
  /**
   * Creates an instance of Product.
   *
   * @param {HTMLElement} el The container of this product
   * @param {Object} config Settings object
   * @param {String} config Section ID
   */
  constructor(el, config = {}) {
    this.el = el;
    this._pruneMobileBlocks();

    this.section = this._getSectionElement(this.el);
    this.sectionId = this.section.id.replace('shopify-section-', '');
    this.config = Object.assign(
      {
        isQuickShop: false,
        isFeaturedProduct: false,
        quickShopUrl: '',
        modal: null,
      },
      config,
    );

    this.formElement = this._getFormElement();

    // Scoped to the product container rather than the form: the variant picker
    // block renders outside the form and binds to it via the `form` attribute,
    // so it is not a descendant the form can query.
    this.addToCartButton = this.el.querySelector(selectors.addToCart);
    this.optionInputs = this.el.querySelectorAll(selectors.optionInput);
    this.variantPicker = this.el.querySelector(selectors.variantPicker);

    this.complementaryProducts = this.el.querySelector(
      selectors.complementaryProducts,
    );

    this._initComponents();
  }

  /**
   * Init all components that make up the product
   */
  _initComponents() {
    this.productImages = new ProductImages(this.el);
    this._initProductForm();

    if (this.complementaryProducts) {
      this._initComplementaryProducts();
    }
  }

  _initProductForm() {
    this.addToCartButton?.addEventListener(
      'click',
      this._onAddToCart.bind(this),
    );

    this.optionInputs?.forEach(optionInput => {
      optionInput.addEventListener('change', this._onOptionChange.bind(this));
    });

    if (this.formElement) {
      new Quantity(this.formElement);
    }
  }

  _initComplementaryProducts() {
    this.productId = this.complementaryProducts.dataset.productId;
    // TODO: create lazy data pattern as helper
    const observer = new IntersectionObserver(
      this._fetchComplementaryProducts,
      {
        rootMargin,
      },
    );

    observer.observe(this.complementaryProducts);
  }

  _getSectionElement = el => {
    return el.closest('.shopify-section');
  };

  /**
   * Resolve the product form, preferring the copy outside the mobile blocks
   * region. Blocks are rendered twice (mobile and desktop) and a block nested
   * in a group marked mobile-above survives pruning, so a duplicate form can
   * exist. Picking deterministically keeps `form` attribute bindings correct.
   *
   * @returns {HTMLFormElement|null}
   */
  _getFormElement() {
    const forms = Array.from(this.el.querySelectorAll(selectors.form));

    if (!forms.length) {
      return null;
    }

    return (
      forms.find(form => !form.closest(selectors.mobileBlocks)) || forms[0]
    );
  }

  /**
   * Remove children without mobile-above class from data-mobile-blocks, and
   * neutralise form bindings in the mobile-above blocks that remain so their
   * duplicate ids cannot capture `form=` or `<label for>` references.
   */
  _pruneMobileBlocks() {
    const mobileBlocks = this.el.querySelector(selectors.mobileBlocks);
    if (!mobileBlocks) {
      return;
    }

    Array.from(mobileBlocks.children).forEach(child => {
      if (!child.classList.contains('mobile-above')) {
        child.remove();
        return;
      }

      this._neutraliseDuplicateForms(child);
    });
  }

  /**
   * Strip ids and form bindings from a duplicated subtree that is kept on screen
   *
   * @param {HTMLElement} root
   */
  _neutraliseDuplicateForms(root) {
    root.querySelectorAll(selectors.form).forEach(form => form.remove());

    // The references have to go along with the ids. Once the duplicate's own
    // ids are gone, a leftover `for`/`aria-controls` resolves against the
    // desktop copy instead, so tapping a label here would drive the hidden
    // control over there.
    const references = [
      'id',
      'form',
      'for',
      'aria-controls',
      'aria-labelledby',
      'aria-describedby',
    ];

    root
      .querySelectorAll(references.map(attr => `[${attr}]`).join(', '))
      .forEach(el => references.forEach(attr => el.removeAttribute(attr)));
  }

  _fetchComplementaryProducts = (entries, observer) => {
    if (!entries[0].isIntersecting) {
      return;
    }

    observer.unobserve(this.complementaryProducts);
    const url = `${Shopify.routes.root}recommendations/products?section_id=${this.sectionId}&limit=${limit}&product_id=${this.productId}&intent=complementary`;

    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const complementary = html.querySelector(
          selectors.complementaryProducts,
        );

        if (complementary && complementary.innerHTML.trim().length) {
          this.complementaryProducts.innerHTML = complementary.innerHTML;
          this.productAccessories?.destroy();
          this.productAccessories = new ProductAccessories(
            this.complementaryProducts,
          );
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  };

  _onAddToCart = e => {
    e.preventDefault();

    if (!this.formElement) {
      return;
    }

    const formData = new FormData(this.formElement);
    const data = cartApi.formatFormData(formData);

    this._toggleLoading(true, getTranslation('products.adding'));

    cartApi
      .add({ items: [data] })
      .then(response => {
        this._toggleLoading(false, getTranslation('products.added'));
        this._reRenderSection(this.formElement.dataset.productUrl);

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }

        if (this.config.isQuickShop) {
          closeModal('quickshop');
        }
      })
      .catch(error => {
        console.error(error);
        this._toggleLoading(false, getTranslation('products.error'));
      });
  };

  _getSelectedOptions = () => {
    return Array.from(this.optionInputs)
      .reduce((arr, input) => {
        if (
          input instanceof HTMLInputElement &&
          input.type === 'radio' &&
          input.checked
        ) {
          arr.push(input.dataset.valueId);
        }

        if (input instanceof HTMLSelectElement) {
          const selectedOption = input.options[input.selectedIndex];
          arr.push(selectedOption.dataset.valueId);
        }

        return arr;
      }, [])
      .join(',');
  };

  _onOptionChange = e => {
    let productUrl =
      this.formElement?.dataset.productUrl || this.el.dataset.productUrl;
    let variantId;
    let isCombinedListing = false;

    if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
      // Check if changed option has associated product URL [Combined Listings]
      const { variantId: optionVariantId, productUrl: optionProductUrl } =
        e.target.dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    if (e.target instanceof HTMLSelectElement) {
      const { productUrl: optionProductUrl, variantId: optionVariantId } =
        e.target.options[e.target.selectedIndex].dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    let newURL;

    if (isCombinedListing) {
      newURL = productUrl;
    } else {
      newURL = new URL(window.location.href);
      newURL.searchParams.set('variant', variantId);
      newURL = newURL.toString();
    }

    this._toggleLoading(true, getTranslation('products.loading'));
    this._reRenderSection(productUrl);

    if (!this.config.isQuickShop && !this.config.isFeaturedProduct) {
      this._appendHistory(newURL);
    }
  };

  _reRenderSection = url => {
    if (this.config.isQuickShop) {
      url = this.config.quickShopUrl;
    } else {
      url = `${url}?section_id=${this.sectionId}`;
    }

    const selectedOptions = this._getSelectedOptions();

    if (selectedOptions.length) {
      url += `&option_values=${selectedOptions}`;
    }

    fetch(url)
      .then(response => response.text())
      .then(data => {
        if (this.config.isQuickShop) {
          const quickshopHtml = showQuickshop(data);
          const quickShopContent = document.getElementById(
            selectors.modalContent,
          );
          quickShopContent.innerHTML = quickshopHtml;

          const product = quickShopContent.querySelector(
            selectors.modalProduct,
          );

          // Create a new Product instance for the requested quickshop product
          new Product(product, this.config);
        } else {
          this.section.outerHTML = data;
          this.section = document.getElementById(
            `shopify-section-${this.sectionId}`,
          );
          const product = this.section.querySelector('[data-product]');
          new Product(product, this.config);
        }
      });
  };

  _appendHistory(url) {
    window.history.pushState({ path: url }, '', url);
  }

  _toggleLoading(loading = false, buttonText) {
    if (loading) {
      this.formElement?.classList.add('loading');
      this.variantPicker?.classList.add('loading');
      this.optionInputs?.forEach(optionInput => (optionInput.disabled = true));

      if (this.addToCartButton) {
        this.addToCartButton.disabled = true;
      }
    } else {
      this.formElement?.classList.remove('loading');
      this.variantPicker?.classList.remove('loading');
      this.optionInputs?.forEach(optionInput =>
        optionInput.removeAttribute('disabled'),
      );
      this.addToCartButton?.removeAttribute('disabled');
    }

    if (buttonText && this.addToCartButton) {
      this.addToCartButton.innerHTML = buttonText;
    }
  }
}
